import { Trans, useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import type { CreditsafeStatus, CreditRating as TCreditRating } from '~/api/clients/types';

import { ExternalLink, Infotip, Spinner } from '~/components';
import { statusColorThemes } from '~/styles/themes.css';
import { clsx } from '~/styles/utils';

import { explanationWrapperStyle, headStyle, labelStyle, labelWrapperStyle, pillStyle, wrapperStyle } from './CreditRating.css';

type Props = {
  creditRating?: TCreditRating;
  creditsafeStatus: CreditsafeStatus;
};

export const CreditRating = ({ creditRating = '?', creditsafeStatus }: Props) => {
  const { t } = useTranslation(['clients']);

  const creditRatingTheme =
    creditsafeStatus === 'Success'
      ? match(creditRating)
          .with('A', 'B', () => statusColorThemes.success)
          .with('C', () => statusColorThemes.warning)
          .with('D', () => statusColorThemes.danger)
          .otherwise(() => statusColorThemes.neutral)
      : statusColorThemes.neutral;

  const ariaLabel =
    creditsafeStatus !== 'Success'
      ? `${t('clients:detail.info.creditsafe.creditRating.label')}: ${t(`clients:detail.info.creditsafe.status.${creditsafeStatus}`)}`
      : `${t('clients:detail.info.creditsafe.creditRating.label')}: ${creditRating} - ${t(`clients:detail.info.creditsafe.score.${creditRating}.text`)}`;

  return (
    <div className={clsx(wrapperStyle, creditRatingTheme)}>
      <div className={labelWrapperStyle}>
        <span className={labelStyle}>{t('clients:detail.info.creditsafe.creditRating.label')}</span>

        <Infotip>
          <Trans components={{ faqLink: <ExternalLink /> }} i18nKey="clients:detail.info.creditsafe.creditRating.tooltip" />
        </Infotip>
      </div>

      <div aria-label={ariaLabel} className={pillStyle}>
        <div className={headStyle}>{creditsafeStatus === 'NotFetched' ? <Spinner size="inline" /> : creditRating}</div>

        <span className={explanationWrapperStyle}>
          {creditsafeStatus === 'Success'
            ? t(`clients:detail.info.creditsafe.score.${creditRating}.text`)
            : t(`clients:detail.info.creditsafe.status.${creditsafeStatus}`)}
        </span>
      </div>
    </div>
  );
};
